// The one way the web application reaches the API (ZENITH.md §17.1): a typed
// client over the generated schema. Paths are checked against api/openapi.yaml
// in CI by tools/uiclientcheck.
//
// A 401 anywhere dispatches "zenith:unauthenticated" on window rather than
// redirecting, so the shell decides what to do (show sign-in, come back after).
import createClient, { type Middleware } from "openapi-fetch";
import { currentRead } from "./read";
import type { paths } from "./schema";
import { countWrites, writeEpoch } from "./writes";
import { recordCalls } from "../report/capture";

export const UNAUTHENTICATED = "zenith:unauthenticated";

const onUnauthenticated: Middleware = {
  async onResponse({ response, request }) {
    // The two doors answer 401 for a refusal, which is the page's to show
    // rather than a session that ended.
    if (response.status === 401 && !request.url.endsWith("/v1/auth/login") && !request.url.endsWith("/v1/auth/sessions")) {
      window.dispatchEvent(new Event(UNAUTHENTICATED));
    }
    return response;
  },
};

// Absolute so the Request is valid wherever fetch runs (a browser resolves
// "/v1" itself; Node, which the tests run in, does not).
const base = typeof window !== "undefined" ? window.location.origin + "/v1" : "/v1";
const client = createClient<paths>({
  baseUrl: base,
  credentials: "same-origin",
  // Resolved per call, not captured at load, so a test can stub fetch.
  fetch: (input) => globalThis.fetch(input),
});
client.use(onUnauthenticated);
// The operator's own writes, counted for the shell's re-reads (`writes.ts`, #1811).
client.use(countWrites);
// Each call's method, path template, status, time and request id, kept in
// memory for a person's in-app report and sent only if they send one
// (ADR-0518 3a, `report/capture.ts`).
client.use(recordCalls);

// **A read `useQuery` asks for is stopped when it is no longer wanted, and is
// shared while an identical one is in flight** (ADR-0419 decision 1, `1d`,
// #1861). The hook says which run of its effect is asking (`read.ts`); a
// request started while it asks is sent through `sharedFetch` with that run's
// signal, and every other request is sent exactly as before.
//
// **The request's identity is the request itself**: its method, its URL with
// the query, every header — `Zenith-Unattended` among them, so a person's read
// never answers an unattended one (ADR-0192 decision 3b) — and its body. Not
// the hook's `deps`: `[siteId]` keys a site's devices and its networks alike,
// and only the request tells them apart.
//
// **Nothing is held once the request answers.** The entry is forgotten when
// the response arrives, so the next asker reads again: this is not a cache
// (ADR-0419 decision 2, ADR-0081 `2a`, ZENITH.md §18 *"looks names up live"*).
// And a read joins only one started since the operator's last write, because
// a read started before a save cannot show it (`writes.ts`, #1811).
//
// **The request is stopped when the last run waiting on it is gone**, never
// sooner: one component leaving does not stop the read another still waits
// on. Each run that leaves before the answer is refused with its own abort,
// and `useQuery` shows nothing for it.
type InFlight = { epoch: number; stop: AbortController; answer: Promise<Response>; joined: number; waiting: number };
const inFlight = new Map<string, InFlight>();

async function identity(request: Request): Promise<string> {
  const headers = [...request.headers].sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  const body = request.body ? await request.clone().text() : "";
  return JSON.stringify([request.method, request.url, headers, body]);
}

function sharedFetch(signal: AbortSignal): (request: Request) => Promise<Response> {
  return async (request) => {
    signal.throwIfAborted();
    const key = await identity(request);
    signal.throwIfAborted();
    let entry = inFlight.get(key);
    if (!entry || entry.epoch !== writeEpoch()) {
      const stop = new AbortController();
      const started: InFlight = { epoch: writeEpoch(), stop, answer: globalThis.fetch(new Request(request, { signal: stop.signal })), joined: 0, waiting: 0 };
      const forget = () => { if (inFlight.get(key) === started) inFlight.delete(key); };
      started.answer.then(forget, forget);
      inFlight.set(key, started);
      entry = started;
    }
    const shared = entry;
    shared.joined++;
    shared.waiting++;
    return new Promise<Response>((resolve, reject) => {
      let answered = false;
      const leave = () => {
        if (!answered) { answered = true; shared.waiting--; reject(signal.reason); }
        if (--shared.joined === 0) {
          if (inFlight.get(key) === shared) inFlight.delete(key);
          shared.stop.abort();
        }
      };
      signal.addEventListener("abort", leave, { once: true });
      shared.answer.then(
        (response) => {
          if (answered) return;
          answered = true;
          // A body is read once, so each run but the last takes a copy and
          // the last takes the response itself.
          resolve(--shared.waiting === 0 ? response : response.clone());
        },
        (error: unknown) => {
          if (answered) return;
          answered = true;
          shared.waiting--;
          reject(error);
        },
      );
    });
  };
}

/** `call`, with a request started while a `useQuery` run asks sent through
 * `sharedFetch`. A call that names its own `signal` or `fetch` is left as it
 * is: whoever wrote it has said how it is stopped. */
function asked<F>(call: F, initAt: number): F {
  const send = call as unknown as (...args: unknown[]) => unknown;
  return ((...args: unknown[]) => {
    const read = currentRead();
    const init = args[initAt] as { signal?: unknown; fetch?: unknown } | undefined;
    if (!read || init?.signal !== undefined || init?.fetch !== undefined) return send(...args);
    const next = [...args];
    next[initAt] = { ...init, fetch: sharedFetch(read.signal) };
    return send(...next);
  }) as unknown as F;
}

export const api: typeof client = {
  ...client,
  request: asked(client.request, 2),
  GET: asked(client.GET, 1),
  PUT: asked(client.PUT, 1),
  POST: asked(client.POST, 1),
  DELETE: asked(client.DELETE, 1),
  OPTIONS: asked(client.OPTIONS, 1),
  HEAD: asked(client.HEAD, 1),
  PATCH: asked(client.PATCH, 1),
  TRACE: asked(client.TRACE, 1),
};

/** Forgets every read in flight, for a test that must not join the read the
 * test before it left out. */
export function forgetSharedReads(): void {
  inFlight.clear();
}

/** The header that marks a read no person asked for, or nothing: a timer's
 * read, or a re-read after the server announced a change, is answered as any
 * other and is not use — it does not keep the session alive against
 * OPERATOR_SESSION_IDLE_TIMEOUT (ADR-0192 decision 3b; api/openapi.yaml,
 * `components.parameters.Unattended`). Spread into an operation's
 * `params.header`; `undefined` sends no header, and the read is use. */
export function unattended(how: { unattended: boolean } | undefined): { "Zenith-Unattended": "true" } | undefined {
  return how?.unattended ? { "Zenith-Unattended": "true" } : undefined;
}

/** A fresh Idempotency-Key for one submission (ZENITH.md §17.1). */
export function idempotencyKey(): string {
  return "web-" + crypto.randomUUID();
}
